import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useGetFuelCardLedgerQuery } from "../fuelApi";
import { useCanReadFuelOffice } from "../useFuelPermissions";
import { formatDateTime, money } from "../fuelFormat";
import { paths } from "../../../routes/paths";

const tile = { background: "#F8F9FA", border: "1px solid #E6E9EC", borderRadius: 6, padding: "6px 10px", minWidth: 150 };
const tileLabel = { fontSize: 10.5, letterSpacing: 0.4, color: "#5A6472", textTransform: "uppercase" };
const tileValue = { fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums" };

/**
 * What the card was used for since its previous refill - opened under a refill row
 * (2026-09-22, "before approval they get a detail of card usage from last refill
 * onwards"). Built from the card's own ledger (approved refills in, non-voided purchases
 * out, newest first): the window runs from the previous APPROVED refill up to this refill's
 * approval - or up to now while it is still awaiting a decision, which is the case the
 * approver cares about.
 */
const RefillUsagePanel = ({ refill }) => {
  const canRead = useCanReadFuelOffice();
  const { data: ledger = [], isLoading, isError } = useGetFuelCardLedgerQuery({ fuelCardId: refill.fuelCardId }, { skip: !canRead });

  const view = useMemo(() => {
    const entries = [...ledger].sort((a, b) => new Date(a.entryDate) - new Date(b.entryDate)); // oldest first
    const isApproved = refill.status === "APPROVED" && refill.approvedDate;
    const windowEnd = isApproved ? new Date(refill.approvedDate) : null; // null = now
    const inWindowEnd = (d) => !windowEnd || new Date(d) < windowEnd;
    // The previous approved refill: the last REFILL entry before this refill (or before now).
    const previous = entries
      .filter((e) => e.entryType === "REFILL" && e.recordGuid !== refill.recordGuid && inWindowEnd(e.entryDate))
      .pop() || null;
    const windowStart = previous ? new Date(previous.entryDate) : null;
    const purchases = entries
      .filter((e) => e.entryType === "PURCHASE" && inWindowEnd(e.entryDate) && (!windowStart || new Date(e.entryDate) >= windowStart))
      .reverse(); // newest first for reading
    const spent = purchases.reduce((s, p) => s + Number(p.amountOut || 0), 0);
    const balanceBefore = isApproved ? refill.previousBalance : refill.cardCurrentBalance;
    return { previous, purchases, spent, balanceBefore, windowEnd };
  }, [ledger, refill]);

  if (!canRead) return <p className="text-muted small mb-0 p-2">Card usage needs a fuel office permission to view.</p>;
  if (isLoading) return <p className="text-muted small mb-0 p-2">Loading card usage…</p>;
  if (isError) return <p className="text-danger small mb-0 p-2">Could not load this card's ledger.</p>;

  const { previous, purchases, spent, balanceBefore, windowEnd } = view;
  const isPending = refill.status === "SUBMITTED";

  return (
    <div className="p-2" style={{ background: "#FBFCFD" }}>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <div style={tile}>
          <div style={tileLabel}>Previous refill</div>
          <div style={tileValue}>{previous ? money(previous.amountIn) : "none"}</div>
          <div className="small text-muted">{previous ? `${previous.documentNumber} · ${formatDateTime(previous.entryDate)}` : "first refill on this card"}</div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>Purchases since</div>
          <div style={tileValue}>{purchases.length}</div>
          <div className="small text-muted">{windowEnd ? `up to ${formatDateTime(windowEnd)}` : "up to now"}</div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>Spent since</div>
          <div style={{ ...tileValue, color: "#9A1B1B" }}>{money(spent)}</div>
          <div className="small text-muted">non-voided purchases</div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>{isPending ? "Card balance now" : "Balance before this refill"}</div>
          <div style={tileValue}>{balanceBefore == null ? "-" : money(balanceBefore)}</div>
          <div className="small text-muted">{isPending ? `this refill would add ${money(refill.refillAmount)}` : `after: ${refill.newBalance == null ? "-" : money(refill.newBalance)}`}</div>
        </div>
      </div>

      {purchases.length === 0 ? (
        <p className="text-muted small mb-0">No purchases on this card since the previous refill.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mb-0" style={{ fontSize: 12.5 }}>
            <thead>
              <tr className="text-muted">
                <th>Date</th>
                <th>Purchase</th>
                <th>Vehicle · litres · station</th>
                <th>By</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.recordGuid}>
                  <td className="text-nowrap">{formatDateTime(p.entryDate)}</td>
                  <td>
                    <Link to={`${paths.fuelTransactions}?open=${p.recordGuid}`} title="Open this purchase with its photos">{p.documentNumber}</Link>
                  </td>
                  <td>{p.description || "-"}</td>
                  <td>{p.recordedByName || "-"}</td>
                  <td className="text-end">{money(p.amountOut)}</td>
                  <td className="text-end">{p.balanceAfter == null ? "-" : money(p.balanceAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RefillUsagePanel;
